import { z } from 'zod';

const OVERRIDE_KEY = 'orderhub:config-override';

const overrideSchema = z.object({
  convexUrl: z.url().optional(),
  cdnUrl: z.url().optional(),
  convexSiteUrl: z.url().optional(),
});

export type WebConfigOverride = z.output<typeof overrideSchema>;

/**
 * Reads the `orderhub:config-override` localStorage entry and returns
 * validated partial overrides for backend-related URLs. Returns `null`
 * when no override is set or when the stored value is invalid.
 *
 * Intended for e2e tests and local development where the frontend
 * needs to talk to a different backend (e.g. local SQLite Convex).
 */
export const getConfigOverride = (): WebConfigOverride | null => {
  try {
    const raw = localStorage.getItem(OVERRIDE_KEY);
    if (!raw) return null;
    const parsed = overrideSchema.parse(JSON.parse(raw));
    if (Object.keys(parsed).length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
};

/** Applies the override on top of a build-time default, returning the override value if set. */
export const resolveWithOverride = (
  buildTimeValue: string | undefined,
  overrideValue: string | undefined,
): string => {
  const resolved = overrideValue ?? buildTimeValue;
  if (!resolved)
    throw new Error('resolveWithOverride: build-time value is not set and no override provided');
  return resolved;
};
